import { PlayIcon, SyncIcon } from 'components/Icons';
import LayoutContext from 'components/Layout/LayoutContext';
import BreadCrumb, {
  IBreadCrumbItem,
} from 'components/__ASSESSOR__/BreadCrumb/BreadCrumb';
import AssembleiaDateTime from 'components/__SHARED__/AssembleiaDateTime';
import InfoCard from 'components/__SHARED__/LabelValueCard/LabelValueCard';
import Page from 'components/__SHARED__/PrivateContainer';
import { parseISO } from 'date-fns';
import { AssembleiaPautaStatusString } from 'helpers/enums/AssembleiaPautaStatus';
import { AssembleiaStatus } from 'helpers/enums/AssembleiaStatus';
import { AssembleiaTipo } from 'helpers/enums/AssembleiaTipo';
import { getUniqueKey } from 'helpers/utils/KeyUtils';
import { languageValues } from 'helpers/utils/languageUtils';
import {
  AssembleiaAssessorRoutePath,
  AssessorRoutePath,
  AssessorTransmissaoRoutePath,
} from 'library/Routes';
import {
  BarChartIcon,
  Button,
  ButtonThemes,
  Col,
  ConfirmModal,
  EditIcon,
  Etiqueta,
  EtiquetasStyle,
  Modal,
  ModalManager,
  ModalSizes,
  RefreshIcon,
  Row,
  ScreenSize,
  Title,
  ToastTypes,
} from 'plataforma-fundacao-componentes';
import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IReducer } from 'store/Reducer';
import { history } from 'store/Store';
import {
  assembleiasAssessorActionsLiberarResultadoPresencial,
  assembleiasAssessorActionsLiberarVotacaoPresencial,
} from 'store/modules/AssembleiasAssessorActions';
import { closeModal, openModal } from 'store/modules/ModalActions';
import Context from '../../Context';
import Pautas from '../../Pautas';
import useStyle from '../../style';

import { AssemblyModalitiesNumber } from 'helpers/enums/Assembleia';
import dotsOnLargeText from 'helpers/utils/dotsOnLargeText';
import translation from 'locales/translation';
import styles from '../PautasDoEvento/styles.module.scss';

export default function VotacaoPresencial() {
  const dispatch = useDispatch();
  const {
    evento,
    usuario,
    presencas,
    pagePautas,
    loadingEvento,
    atualizarPresencas,
    atualizandoPresencas,
    editarTransmissao,
    setPagePautas,
    loadPautas,
  } = useContext(Context);

  const EventoModalRef = useRef<any>();

  const { showToast } = useContext(LayoutContext);

  const isAGN = useMemo(() => {
    if (!evento?.tipo) return false;
    return [AssembleiaTipo.Nucleo, AssembleiaTipo.Reuniao].includes(
      evento.tipo
    );
  }, [evento]);
  const classes = useStyle({ isAGN });

  const eventName = useMemo(() => {
    if (loadingEvento) {
      return translation.AssembleiaAssessorRoute.carregando;
    }
    return evento?.nome || '';
  }, [evento, loadingEvento]);

  const breadcrumb = useMemo(
    (): IBreadCrumbItem[] => [
      { nome: translation.MainRoute.title, url: AssessorRoutePath },
      {
        nome: dotsOnLargeText(eventName, 30),
        url: `${AssembleiaAssessorRoutePath.replace(
          ':id',
          String(evento?.id)
        )}`,
      },
      { nome: 'Votação Presencial' },
    ],
    [eventName, evento?.id]
  );

  const eventStatus = useMemo(() => {
    if (loadingEvento) {
      return translation.AssembleiaAssessorRoute.carregando;
    }
    return translation.AssembleiaStatus[`${evento?.status || 0}`];
  }, [evento, loadingEvento]);

  const needQuorumRegister = useMemo(() => {
    return (
      evento?.status === AssembleiaStatus.Live &&
      evento.tipo !== AssembleiaTipo.Nucleo &&
      evento.tipo !== AssembleiaTipo.Reuniao &&
      !evento.existeQuorum
    );
  }, [evento]);

  const { pautas } = useSelector((state: IReducer) => state.assessor);

  const openTransmissao = useCallback(() => {
    if (!evento?.urlTransmissao) return;
    const winUrl =
      AssessorTransmissaoRoutePath + `?url=${evento.urlTransmissao}`;
    const win = window.open(winUrl, '_blank');
    win?.focus();
  }, [evento]);

  const infoModal = useCallback(
    (title: string, description: React.ReactNode | string) => {
      const mKey = `alert${getUniqueKey()}`;
      dispatch(
        openModal(Modal, {
          preventMaskExit: true,
          size: ModalSizes.Large,
          modalKey: mKey,
          title: title,
          children: <p className={classes.contentModal}>{description}</p>,
        })
      );
    },
    [dispatch, classes.contentModal]
  );

  const startModal = useCallback(() => {
    const mKey = `alert${getUniqueKey()}`;
    dispatch(
      openModal(ConfirmModal, {
        size: ModalSizes.Large,
        mobileOn: ScreenSize.xs,
        modalKey: mKey,
        title: translation.AssembleiaAssessorRoute.buttonRegistrarQuorum,
        children: translation.VotoPresencial.modal.iniciarVotacao,
        preventMaskExit: true,
        onConfirm: () => {
          return new Promise<any>((resolve: (evt?: any) => void) => {
            dispatch(
              assembleiasAssessorActionsLiberarVotacaoPresencial(
                evento?.id,
                () => {},
                () => {},
                (err) => {
                  showToast({
                    pauseOnFocusLoss: true,
                    theme: ToastTypes.Error,
                    showStatusBar: true,
                    timeout: 6000,
                    label: languageValues.toasts.error.keyErrors(
                      err!?.response!?.data.message
                    ),
                  });
                }
              )
            );
            dispatch(closeModal(mKey));
            resolve();
          });
        },
        onCancel: () => {
          dispatch(closeModal(mKey));
        },
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evento, dispatch]);

  const showResults = useCallback(() => {
    const mk = getUniqueKey();
    dispatch(
      openModal(ConfirmModal, {
        modalKey: mk,
        size: ModalSizes.Large,
        title: 'Liberar Resultados',
        mobileOn: ScreenSize.xs,
        children:
          'ATENÇÃO! Após liberação dos resultados, não é possível reabrir a votação e nem editar os resultados da votação. Confirma a liberação dos resultados da votação presencial para consolidação com a votação digital?',
        onClose: () => {
          dispatch(closeModal(mk));
        },
        onCancel: () => {
          dispatch(closeModal(mk));
        },
        onConfirm: () => {
          return new Promise<void>((resolve: (evt?: any) => void) => {
            dispatch(
              assembleiasAssessorActionsLiberarResultadoPresencial(
                evento!?.id,
                () => {
                  dispatch(closeModal(mk));
                },
                () => {
                  showToast({
                    pauseOnFocusLoss: true,
                    theme: ToastTypes.Error,
                    showStatusBar: true,
                    timeout: 6000,
                    label: 'Erro ao liberar os resultados presenciais!',
                  });
                }
              )
            );
            resolve();
          });
        },
      })
    );
  }, [dispatch, evento, showToast]);

  const todasLiberadas = useMemo(() => {
    return pautas!?.elements.every(
      (pauta: any) => pauta.resultadoLiberadoPresencial === true
    );
  }, [pautas]);

  const showButtonStartModal = useMemo(() => {
    return pautas!?.elements!?.some((x: any) => {
      return x.statusPresencial === AssembleiaPautaStatusString.Bloqueada;
    });
  }, [pautas]);

  const allFinished = useMemo(() => {
    return pautas!?.elements!?.every((x: any) => {
      return x.statusPresencial === AssembleiaPautaStatusString.Encerrada;
    });
  }, [pautas]);

  useEffect(() => {
    if (
      !loadingEvento &&
      evento?.modo !== AssemblyModalitiesNumber.HIBRIDO_SIMULTANEO
    ) {
      showToast({
        pauseOnFocusLoss: true,
        theme: ToastTypes.Error,
        showStatusBar: true,
        timeout: 6000,
        label: translation.VotoPresencial.accessDeniedError,
      });
      history.push(`${AssessorRoutePath}`);
    }
  }, [loadingEvento, evento, showToast]);

  return (
    <Fragment>
      <Page
        header={
          <Row>
            <Col>
              <Title
                showBackButton
                onBackClick={() => {
                  history.push(
                    `${AssembleiaAssessorRoutePath.replace(
                      ':id',
                      String(evento?.id)
                    )}`
                  );
                }}
                rightElement={
                  allFinished ? undefined : showButtonStartModal ? undefined : (
                    <Etiqueta
                      icon={<EditIcon />}
                      label={'Em votação'}
                      theme={EtiquetasStyle.Edicao}
                    />
                  )
                }
              >
                Votação Presencial
              </Title>
            </Col>
            <Col noPaddingTop>
              <BreadCrumb itens={breadcrumb} />
            </Col>
          </Row>
        }
        headerClassName={classes.header}
        contentClassName={styles.root}
      >
        <Row>
          <Row style={{ alignItems: 'center' }}>
            <Col cols={[12, 4, 3, 3, 2]}>
              <div>
                {translation.AssembleiaAssessorRoute.situacao(eventStatus)}
              </div>
            </Col>
            <Col cols={[12, 8, 6, 6, 8]}>
              {evento?.dataEvento && (
                <AssembleiaDateTime
                  date={parseISO(evento.dataEvento)}
                  alignStart
                />
              )}
            </Col>
            <Col cols={[12, 12, 12, 12, 12]} buttonActionsCol end>
              <React.Fragment>
                {allFinished ? (
                  <Button
                    theme={ButtonThemes.Secondary}
                    color='primary'
                    onClick={() => {
                      showResults();
                    }}
                    leftIcon={<BarChartIcon />}
                    disabled={todasLiberadas}
                  >
                    {translation.AssembleiaPauta.acoes.liberarResultados}
                  </Button>
                ) : showButtonStartModal ? (
                  <Button
                    theme={ButtonThemes.Primary}
                    color='primary'
                    onClick={() => {
                      startModal();
                    }}
                    leftIcon={<EditIcon />}
                  >
                    {translation.AssembleiaAssessorRoute.buttonRegistrarQuorum}
                  </Button>
                ) : undefined}
              </React.Fragment>
            </Col>
          </Row>
          <Col style={{ marginTop: '16px' }}>
            <Button
              data-testid={'assessor-atualizar-participantes-button'}
              theme={ButtonThemes.Secondary}
              onClick={atualizarPresencas}
              leftIcon={<RefreshIcon animate={false} />}
              disabled={atualizandoPresencas}
              loading={atualizandoPresencas}
            >
              {translation.AssembleiaAssessorRoute.buttonAtualizarParticipantes}
            </Button>
          </Col>
          <Row className={styles.cards}>
            <Col gap={14}>
              <InfoCard
                label={
                  translation.AssembleiaAssessorRoute[
                    evento?.urlTransmissao
                      ? 'buttonAssistirTransmissao'
                      : 'buttonEditarTransmissao'
                  ]
                }
                value={
                  loadingEvento ? (
                    <SyncIcon animated className={classes.labelValueIcon} />
                  ) : (
                    <PlayIcon className={classes.labelValueIcon} />
                  )
                }
                button
                onClick={
                  evento?.urlTransmissao ? openTransmissao : editarTransmissao
                }
              />
              <InfoCard
                label={
                  translation.AssembleiaAssessorRoute.infoCardLabel
                    .quorumAtingido
                }
                infoDescription={() => {
                  infoModal(
                    translation.AssembleiaAssessorRoute.infoCardLabel
                      .quorumAtingido,
                    <>
                      <b>
                        {
                          translation.AssembleiaAssessorRoute.CardModal
                            .quorumAtingidoBold1
                        }
                      </b>
                      {
                        translation.AssembleiaAssessorRoute.CardModal
                          .quorumAtingido1
                      }
                      <b>
                        {
                          translation.AssembleiaAssessorRoute.CardModal
                            .quorumAtingidoBold2
                        }
                      </b>
                      {
                        translation.AssembleiaAssessorRoute.CardModal
                          .quorumAtingido2
                      }
                    </>
                  );
                }}
                value={
                  loadingEvento || atualizandoPresencas
                    ? '-'
                    : translation.AssembleiaAssessorRoute.quorumAtingido[
                        `${presencas.quorumAtingido}`
                      ]
                }
              />
              <InfoCard
                label={
                  translation.AssembleiaAssessorRoute.infoCardLabel
                    .participantes
                }
                infoDescription={() => {
                  infoModal(
                    translation.AssembleiaAssessorRoute.infoCardLabel
                      .participantes,
                    translation.AssembleiaAssessorRoute.CardModal
                      .participantesOnline
                  );
                }}
                value={
                  loadingEvento || atualizandoPresencas
                    ? '-'
                    : presencas.quantidadePessoasOnline
                }
              />
              <InfoCard
                label={translation.AssembleiaAssessorRoute.infoCardLabel.contas}
                infoDescription={() => {
                  infoModal(
                    translation.AssembleiaAssessorRoute.infoCardLabel.contas,
                    translation.AssembleiaAssessorRoute.CardModal.associados
                  );
                }}
                value={
                  loadingEvento || atualizandoPresencas
                    ? '-'
                    : presencas.contasCapital
                }
              />
              {isAGN && (
                <InfoCard
                  label={
                    translation.AssembleiaAssessorRoute.infoCardLabel.aptos
                  }
                  infoDescription={() => {
                    infoModal(
                      translation.AssembleiaAssessorRoute.infoCardLabel.aptos,
                      translation.AssembleiaAssessorRoute.CardModal
                        .associadosAptos
                    );
                  }}
                  value={
                    loadingEvento || atualizandoPresencas
                      ? '-'
                      : presencas.presentesAptos
                  }
                />
              )}
              <InfoCard
                label={
                  translation.AssembleiaAssessorRoute.infoCardLabel.presencas
                }
                infoDescription={() => {
                  infoModal(
                    translation.AssembleiaAssessorRoute.infoCardLabel.presencas,
                    translation.AssembleiaAssessorRoute.CardModal
                      .pessoasPresentes
                  );
                }}
                value={
                  loadingEvento || atualizandoPresencas
                    ? '-'
                    : presencas.presentes
                }
              />
              <InfoCard
                label={'Convidados'}
                infoDescription={() => {
                  infoModal(
                    'Convidados presentes',
                    'Pessoas que estão assistindo a assembleia, mas não constam na lista de convocados deste evento. Ou seja, não possuem direito a voto e nem confirmaram presença. Verificar em presentes > convidados a lista das pessoas.'
                  );
                }}
                value={
                  loadingEvento || atualizandoPresencas
                    ? '-'
                    : presencas.convidados
                }
              />
            </Col>
          </Row>
          {usuario?.perfil.assessorRoute.assembleias.pautas.ver &&
            evento?.id && (
              <Row>
                <Col>
                  <Pautas
                    idEvento={evento.id}
                    needQuorumRegister={needQuorumRegister}
                    currentPage={pagePautas + 1}
                    onChangePage={(value: number) => {
                      if (value - 1 !== pagePautas) {
                        setPagePautas(value - 1);
                      } else {
                        loadPautas(evento!?.id);
                      }
                    }}
                    presential={true}
                  />
                </Col>
              </Row>
            )}
        </Row>
      </Page>
      <ModalManager ref={EventoModalRef} />
    </Fragment>
  );
}
